import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Toast } from 'primereact/toast';
// import { StatCardSkeleton, TableRowSkeleton } from '../../components/common';
import QuoteDetailDrawer from '../quote/QuoteDetailDrawer';
import { StatCardSkeleton, TableRowSkeleton } from '../../components/common/Skeleton';

const avatarColors = [
  '#1a7a3a', '#0d6efd', '#6f42c1', '#d97706',
  '#0891b2', '#be185d', '#059669', '#7c3aed',
];

function getAvatarColor(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return avatarColors[Math.abs(hash) % avatarColors.length];
}

function getInitials(name: string) {
  return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [drawerQuote, setDrawerQuote] = useState<any>(null);
  const [now] = useState(new Date());
  const [allQuotes, setAllQuotes] = useState<any[]>([]);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    const token = localStorage.getItem('igtoken');
    axios({
      method: 'get',
      url: `${process.env.REACT_APP_TRUCKING_API_Url}/api/account/quotelist`,
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res: any) => {
        const mapped = (res.data || []).map((item: any) => ({
          id:      item.transactionId,
          quoteId: item.quoteId ?? '',
          company: item.companyName || '',
          date:    item.effectiveDate || '',
          vehicleCount: item.vehicleCount ?? 0,
          driverCount:  item.driverCount ?? 0,
          status:  item.status,
          contact: item.insuredName || '',
        }));
        setAllQuotes(mapped);
      })
      .catch((error: any) => {
        if (error.response?.status === 401) {
          localStorage.clear();
          navigate("/login");
        } else {
          setAllQuotes([]);
          toast.current?.show({
            severity: 'error', summary: 'Error',
            detail: error.response?.data?.message
              ? `Error ${error.response.status}: ${error.response.data.message}`
              : 'Failed to load quotes. Please try again.',
            life: 3000,
          });
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [navigate]);

  const recentQuotes = allQuotes.slice(0, 5);

  const dashboardStats = {
    totalQuotes:    allQuotes.length,
    pendingQuotes:  allQuotes.filter((q: any) => q.status === 'Pending').length,
    approvedQuotes: allQuotes.filter((q: any) => q.status === 'Approved').length,
    rejectedQuotes: allQuotes.filter((q: any) => q.status === 'Rejected').length,
  };

  const getStatusClass = (s: string) => {
    const map: Record<string, string> = {
      Approved: 'badge badge--approved',
      Pending:  'badge badge--pending',
      Rejected: 'badge badge--rejected',
    };
    return map[s];
  };

  const filteredQuotes = recentQuotes.filter((q: any) =>
    (q.contact || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (q.company || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (q.quoteId || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const dateStr = now.toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });

  const statsData = [
    {
      label: 'Total Submissions',
      value: dashboardStats.totalQuotes,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
        </svg>
      ),
      accent: '#1a7a3a', bg: '#f0f9f4', trend: '+12%', trendUp: true,
    },
    {
      label: 'Pending Review',
      value: dashboardStats.pendingQuotes,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
        </svg>
      ),
      accent: '#d97706', bg: '#fffbeb', trend: '-3', trendUp: false,
    },
    {
      label: 'Approved',
      value: dashboardStats.approvedQuotes,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
        </svg>
      ),
      accent: '#059669', bg: '#ecfdf5', trend: '+8%', trendUp: true,
    },
    {
      label: 'Declined',
      value: dashboardStats.rejectedQuotes,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
      ),
      accent: '#dc2626', bg: '#fef2f2', trend: '-2', trendUp: true,
    },
  ];

  const quickActions = [
    // {
    //   label: 'Start New Quote',
    //   desc: 'Begin a commercial trucking submission',
    //   accent: '#1a7a3a',
    //   bg: '#f0f9f4',
    //   border: '#c6e0cd',
    //   path: '/new-quote',
    //   icon: (
    //     <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    //       <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
    //     </svg>
    //   ),
    // },
    {
      label: 'Quote History',
      desc: 'Browse and manage all submissions',
      accent: '#0d6efd',
      bg: '#eff6ff',
      border: '#bfdbfe',
      path: '/quote-history',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
        </svg>
      ),
    },
    // {
    //   label: 'Manage Clients',
    //   desc: 'View and organize your client list',
    //   accent: '#6f42c1',
    //   bg: '#f5f3ff',
    //   border: '#ddd6fe',
    //   path: '/clients',
    //   icon: (
    //     <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    //       <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    //       <circle cx="9" cy="7" r="4"/>
    //       <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    //     </svg>
    //   ),
    // },
  ];

  return (
    <div className="db">
      <Toast ref={toast} position="top-right" />

      {/* ── Welcome Banner ── */}
      <motion.div
        className="db-welcome"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.38, ease: 'easeOut' }}
      >
        {/* Decorative truck silhouette */}
        <div className="db-welcome__deco" aria-hidden>
          <svg width="160" height="80" viewBox="0 0 160 80" fill="none" opacity="0.07">
            <rect x="4" y="24" width="90" height="40" rx="6" fill="white"/>
            <path d="M94 40 H130 L148 60 V64 H94 V40Z" fill="white"/>
            <circle cx="28" cy="68" r="10" fill="white"/>
            <circle cx="76" cy="68" r="10" fill="white"/>
            <circle cx="136" cy="68" r="10" fill="white"/>
          </svg>
        </div>
        <div className="db-welcome__left">
          <div className="db-welcome__eyebrow">Commercial Trucking Portal</div>
          <h1 className="db-welcome__greeting">{getGreeting()}, Underwriter</h1>
          <p className="db-welcome__sub">Here's your underwriting overview for today.</p>
        </div>
        <div className="db-welcome__right">
          <div className="db-welcome__date">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            {dateStr}
          </div>
          {/* <button className="db-welcome__cta" onClick={() => navigate('/new-quote')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            New Quote
          </button> */}
        </div>
      </motion.div>

      {/* ── Stat Cards ── */}
      <div className="db-stats">
        {isLoading
          ? [1,2,3,4].map(i => <StatCardSkeleton key={i} />)
          : statsData.map((s, i) => (
            <motion.div
              key={s.label}
              className="db-stat"
              style={{ '--accent': s.accent, '--accent-bg': s.bg } as React.CSSProperties}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07, duration: 0.35, ease: 'easeOut' }}
            >
              <div className="db-stat__top-bar" />
              <div className="db-stat__body">
                <div className="db-stat__icon-wrap">{s.icon}</div>
                <div className="db-stat__info">
                  <span className="db-stat__label">{s.label}</span>
                  <span className="db-stat__value">{s.value}</span>
                </div>
              </div>
              <div className="db-stat__footer">
                <span className={`db-stat__trend ${s.trendUp ? 'db-stat__trend--up' : 'db-stat__trend--down'}`}>
                  {s.trendUp
                    ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="18 15 12 9 6 15"/></svg>
                    : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                  }
                  {s.trend}
                </span>
                <span className="db-stat__trend-label">vs last month</span>
              </div>
            </motion.div>
          ))
        }
      </div>

      {/* ── Quick Actions ── */}
      <div className="db-quick-actions">
        {quickActions.map((a, i) => (
          <motion.button
            key={a.label}
            className="db-qa"
            style={{ '--qa-accent': a.accent, '--qa-bg': a.bg, '--qa-border': a.border } as React.CSSProperties}
            onClick={() => navigate(a.path)}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.28 + i * 0.07, duration: 0.32, ease: 'easeOut' }}
          >
            <div className="db-qa__icon">{a.icon}</div>
            <div className="db-qa__body">
              <div className="db-qa__label">{a.label}</div>
              <div className="db-qa__desc">{a.desc}</div>
            </div>
            <div className="db-qa__arrow">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
              </svg>
            </div>
          </motion.button>
        ))}
      </div>

      {/* ── Recent Submissions Table ── */}
      <motion.div
        className="db-table-card"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.48, duration: 0.4, ease: 'easeOut' }}
      >
        {/* Card header */}
        <div className="db-table-card__header">
          <div>
            <h2 className="db-table-card__title">Recent Submissions</h2>
            <p className="db-table-card__sub">Latest commercial trucking submissions</p>
          </div>
          <button className="db-view-all" onClick={() => navigate('/quote-history')}>
            View all
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
            </svg>
          </button>
        </div>

        {/* Toolbar */}
        <div className="db-toolbar">
          <div className="search-box">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              placeholder="Search by ID or company..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="db-toolbar__filters">
            <button className="db-filter-btn">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/>
                <line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/>
                <line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/>
              </svg>
              All Status
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            <button className="db-filter-btn">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              This Month
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="db-table-wrap">
          {isLoading ? (
            [1,2,3,4,5].map(i => <TableRowSkeleton key={i} columns={6} />)
          ) : (
            <table className="db-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Quote ID</th>
                  <th>Date</th>
                  <th>Vehicles</th>
                  <th>Drivers</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredQuotes.map((q: any) => (
                  <tr key={q.id} className={`db-table__row db-table__row--${q.status.toLowerCase()}`}>
                    <td>
                      <div className="company-cell">
                        <span
                          className="company-cell__avatar"
                          style={{ background: getAvatarColor(q.contact || q.company) }}
                        >
                          {getInitials(q.contact || q.company)}
                        </span>
                        <div className="company-cell__info">
                          <span className="company-cell__name">{q.contact || q.company}</span>
                        </div>
                      </div>
                    </td>
                    <td><span className="quote-id">{q.quoteId}</span></td>
                    <td><span className="db-date">{q.date}</span></td>
                    <td><span className="db-premium">{q.vehicleCount}</span></td>
                    <td><span className="db-premium">{q.driverCount}</span></td>
                    <td><span className={getStatusClass(q.status)}>{q.status}</span></td>
                    <td>
                      <button
                        className="db-action-btn"
                        title="View details"
                        onClick={() => setDrawerQuote(q)}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="db-table-footer">
          <span className="db-table-footer__info">
            Showing <strong>{filteredQuotes.length}</strong> of <strong>{recentQuotes.length}</strong> submissions
          </span>
          <div className="db-pagination">
            <button className="page-btn" disabled>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <button className="page-btn page-btn--active">1</button>
            <button className="page-btn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>
        </div>
      </motion.div>

      {/* ── Quote Detail Drawer ── */}
      <QuoteDetailDrawer
        isOpen={drawerQuote !== null}
        onClose={() => setDrawerQuote(null)}
        transactionId={drawerQuote?.id ?? null}
        quoteId={drawerQuote?.quoteId ?? null}
        status={drawerQuote?.status ?? null}
      />
    </div>
  );
};

export default Dashboard;
